import { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { combineClassnames } from 'app/common/presentation'
import { ConnectionName, GoogleAdwordsAdAccount, SupportedAccount } from 'app/connections/types'

import { Checkbox, CodeTag, Table } from 'components'

import styles from './Accounts.module.scss'

interface AccountsProps {
  accounts: SupportedAccount[]
  selectionOptions?: {
    isSelected: (account: SupportedAccount) => boolean
    onClick: (account: SupportedAccount) => void
  }
  connectionName: ConnectionName
  variant?: 'default' | 'selection'
}

type ColumnKey = '' | keyof SupportedAccount

const colummnKeys: Partial<Record<ConnectionName, string[]>> = {
  criteo: ['id', 'name'],
  dv360: ['id', 'name', 'partnerId'],
  facebook: ['id', 'name', 'businessName'],
  googleAdwords: ['id', 'name', 'type', 'managerId'],
  googleAnalytics: ['id', 'name'],
}

const getColumnKeys = (connectionName: ConnectionName, hasSelectColumn: boolean) => {
  const columns = hasSelectColumn ? [''] : []

  if (colummnKeys[connectionName]) {
    columns.push(...colummnKeys[connectionName])
  }

  return columns as ColumnKey[]
}

const isGoogleAdwordsAccount = (account: SupportedAccount): account is GoogleAdwordsAdAccount => {
  return 'isManager' in account
}

export const Accounts = ({
  accounts,
  selectionOptions,
  connectionName: connectionName,
  variant = 'default',
}: AccountsProps) => {
  const { t } = useTranslation('connections')

  const { isSelected, onClick } = selectionOptions ?? { isSelected: () => false }

  const hasSelectColumn = !!onClick

  const columnKeys = useMemo(
    () => getColumnKeys(connectionName, hasSelectColumn),
    [hasSelectColumn, connectionName],
  )

  const renderCell = (account: SupportedAccount, key: ColumnKey) => {
    if (key) {
      if (
        isGoogleAdwordsAccount(account) &&
        (key as keyof (typeof colummnKeys)['googleAdwords']) === 'type'
      ) {
        return (
          <Table.Cell>
            {account.isManager ? 'Manager' : account.managerId ? 'Managed Customer' : 'Customer'}
          </Table.Cell>
        )
      }

      return <Table.Cell>{account[key] ? account[key] : '-'}</Table.Cell>
    } else {
      return (
        <Table.Cell>
          <Checkbox checked={isSelected(account)} />
        </Table.Cell>
      )
    }
  }

  const renderRow = (account: SupportedAccount, index: number) => (
    <Table.Row
      key={index}
      className={isSelected(account) ? styles.selected : undefined}
      onClick={onClick ? () => onClick(account) : undefined}
    >
      {columnKeys.map((columnKey, index) => (
        <Fragment key={index}>{renderCell(account, columnKey)}</Fragment>
      ))}
    </Table.Row>
  )

  const renderSelectionRow = (account: SupportedAccount) => {
    const secondaryKey = columnKeys.find(
      (columnKey) => columnKey && !['id', 'name'].includes(columnKey),
    )

    return (
      <div
        className={styles.accountSelectionRow}
        data-selected={isSelected(account)}
        key={account.id}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') onClick?.(account)
        }}
        onClick={() => onClick?.(account)}
        role="button"
        tabIndex={0}
      >
        <span className={styles.accountCheckbox}>
          <Checkbox checked={isSelected(account)} />
        </span>
        <span className={styles.accountId}>
          <CodeTag color="grey">{account.id}</CodeTag>
        </span>
        <strong>{account.name}</strong>
        <span className={styles.accountSecondary}>
          {secondaryKey && account[secondaryKey] ? account[secondaryKey] : '-'}
        </span>
      </div>
    )
  }

  if (variant === 'selection') {
    return <div className={styles.accountSelectionList}>{accounts.map(renderSelectionRow)}</div>
  }

  return (
    <div className={styles.selectedAccounts}>
      {!hasSelectColumn && <div>{t(`setup.${connectionName}.accountsSelected`)}</div>}
      <Table.Table
        className={combineClassnames(styles.accounts, onClick ? styles.selectable : undefined)}
        columns={columnKeys.map((columnKey) =>
          columnKey ? t(`setup.${connectionName}.account.${columnKey}`) : '',
        )}
        data={accounts}
        renderRow={renderRow}
      />
    </div>
  )
}
